import React from 'react';

/**
 * UOM Modal Component
 * 
 * Fields:
 * - Name: textfield
 * - Symbol: textfield
 * - Default: checkbox
 * - Relation with Default: textfield (only shown when a default item exists and current item is NOT the default)
 */
const UOMModal = ({ show, onClose, onSave, initial = {}, defaultItem = null }) => {
  const [name, setName] = React.useState(initial.name || '');
  const [symbol, setSymbol] = React.useState(initial.symbol || '');
  const [isDefault, setIsDefault] = React.useState(initial.is_default ?? false);
  const [relationWithDefault, setRelationWithDefault] = React.useState(initial.relation_with_default || '');

  React.useEffect(() => {
    setName(initial.name || '');
    setSymbol(initial.symbol || '');
    setIsDefault(initial.is_default ?? false);
    setRelationWithDefault(initial.relation_with_default || '');
  }, [initial]);

  if (!show) return null;

  // Show "Relation with Default" field only when:
  // 1. A default item exists
  // 2. The current item is NOT the default (either editing a non-default item, or creating new while default exists)
  const showRelationField = defaultItem && !isDefault;

  return (
    <div className="modal-backdrop d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{initial.id ? 'Edit' : 'Add'} {initial.title || 'UOM'}</h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">
              {/* Name field */}
              <div className="mb-3">
                <label className="form-label">Name <span className="text-danger">*</span></label>
                <input 
                  className="form-control" 
                  value={name} 
                  onChange={(e) => setName(e.target.value)} 
                  placeholder="e.g., Kilogram, Meter, Liter"
                />
              </div>

              {/* Symbol field */}
              <div className="mb-3">
                <label className="form-label">Symbol <span className="text-danger">*</span></label>
                <input 
                  className="form-control" 
                  value={symbol} 
                  onChange={(e) => setSymbol(e.target.value)} 
                  placeholder="e.g., kg, m, L"
                />
              </div>

              {/* Default checkbox */}
              <div className="form-check form-switch mb-3">
                <input 
                  className="form-check-input" 
                  type="checkbox" 
                  id="modalIsDefault" 
                  checked={isDefault} 
                  onChange={(e) => setIsDefault(e.target.checked)} 
                />
                <label className="form-check-label ms-2" htmlFor="modalIsDefault">Default</label>
                {isDefault && (
                  <small className="d-block text-muted mt-1">
                    This will be the default unit for this UOM type
                  </small>
                )}
              </div>

              {/* Relation with Default - only shown when default exists and current is not default */}
              {showRelationField && (
                <div className="mb-3">
                  <label className="form-label">
                    Relation with Default ({defaultItem.name} - {defaultItem.symbol})
                  </label>
                  <input 
                    className="form-control" 
                    value={relationWithDefault} 
                    onChange={(e) => setRelationWithDefault(e.target.value)} 
                    placeholder="e.g., 1000 (if 1 kg = 1000 g)"
                  />
                  <small className="text-muted">
                    Enter the conversion factor: How many {symbol || 'units'} equal 1 {defaultItem.symbol}
                  </small>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={() => onSave({ 
                  ...initial, 
                  name: name.trim(), 
                  symbol: symbol.trim(),
                  is_default: isDefault,
                  relation_with_default: showRelationField ? relationWithDefault : null,
                  active: true
                })} 
                disabled={!name.trim() || !symbol.trim()}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UOMModal;
